// ============================================================================
// Is a deal redeemable right now? — shared by the feed ("Nu actief"), the deal
// page and the QR pre-check.
//
// "Nu actief" used to mean "expires within 2 hours", so on a normal day it
// showed 0 deals even though plenty were live. It now means what it says: the
// walker can walk in and use this deal at this moment.
// ============================================================================

const ENG_DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const NL_DAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

// "09:00:00" / "09:00" → minutes since midnight, or null.
function toMinutes(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function inWindow(nowMin, from, until) {
  const a = toMinutes(from);
  const b = toMinutes(until);
  if (a == null || b == null) return true; // no window → whole day
  if (a === b) return true;
  // A window that runs past midnight (22:00–02:00) wraps around.
  return a < b ? nowMin >= a && nowMin <= b : nowMin >= a || nowMin <= b;
}

// Today's opening hours, accepting both key styles the platform has used.
function todaysHours(openingHours, date) {
  if (!openingHours || typeof openingHours !== 'object') return null;
  const i = date.getDay();
  return openingHours[ENG_DAYS[i]] || openingHours[NL_DAYS[i]] || null;
}

// Why a deal is (not) available, as a stable code + Dutch message. The QR page
// shows the message; the feed only needs `live`.
export function dealAvailability(deal, now = new Date()) {
  if (!deal) {
    return { live: false, reason: 'missing', message: 'Deze deal bestaat niet meer.' };
  }

  // Normalised status ('actief', 'gepauzeerd', …) or the raw API one.
  const status = String(deal.status || '').toLowerCase();
  if (deal.active === false || status === 'verwijderd' || status === 'deleted') {
    return { live: false, reason: 'ended', message: 'Deze deal is beëindigd door de winkelier en niet meer beschikbaar.' };
  }
  if (status === 'gepauzeerd' || status === 'paused') {
    return { live: false, reason: 'paused', message: 'Deze deal is op dit moment gepauzeerd door de winkelier.' };
  }

  const t = now.getTime();
  const until = deal.expiresAt ?? deal.validUntil;
  if (status === 'verlopen' || status === 'expired' || (until && new Date(until).getTime() <= t)) {
    return { live: false, reason: 'expired', message: 'Deze deal is verlopen en niet meer beschikbaar.' };
  }
  if (deal.validFrom && new Date(deal.validFrom).getTime() > t) {
    return { live: false, reason: 'scheduled', message: 'Deze deal is nog niet gestart.' };
  }

  const nowMin = now.getHours() * 60 + now.getMinutes();
  if (!inWindow(nowMin, deal.validFromTime, deal.validUntilTime)) {
    return { live: false, reason: 'outside-hours', message: 'Deze deal is op dit tijdstip niet geldig.' };
  }

  const hours = todaysHours(deal.openingHours, now);
  if (hours) {
    if (hours.closed) {
      return { live: false, reason: 'shop-closed', message: 'De winkel is vandaag gesloten.' };
    }
    if (hours.open && hours.close && !inWindow(nowMin, hours.open, hours.close)) {
      return { live: false, reason: 'shop-closed', message: 'De winkel is op dit moment gesloten.' };
    }
  }

  return { live: true, reason: 'live', message: '' };
}

export function isDealLiveNow(deal, now = new Date()) {
  return dealAvailability(deal, now).live;
}
